import { useMemo } from 'react'
import classNames from 'classnames'

import Image from '@/components/base/Image'
import ImageParallax from '@/components/base/ImageParallax'
import Link from '@/components/base/Link'
import Typography from '@/components/base/Typography'

import { WPImage, WPLink } from '@/types'

export type ImageCardProps = Partial<{
  uuid: number
  post_type: string
  image: WPImage
  title: string
  badge: string
  overline: string
  paragraph: string
  link: WPLink
}>

const ImageCard = ({
  image,
  title,
  badge,
  overline,
  paragraph,
  post_type,
  link
}: ImageCardProps) => {
  const needsImage = useMemo(() => image && image.url, [image])

  return (
    <div
      className={classNames(
        'relative',
        'before:absolute before:left-0 before:top-0 before:z-0 before:h-full before:w-full before:rounded-xs before:shadow-3xl'
      )}
      data-motion-image-card
    >
      <div
        className={classNames('relative z-10 rounded-xs bg-white', {
          'aspect-h-[420] aspect-w-[393]': needsImage,
          'aspect-h-[300] aspect-w-[393]': !needsImage
        })}
      >
        {needsImage ? (
          <div
            className={classNames(
              'before:absolute before:left-0 before:top-0 before:h-full before:w-full before:rounded-xs before:bg-blue',
              'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:rounded-xs',
              {
                'after:bg-black/30': post_type !== 'person',
                'after:bg-gradient-to-t after:from-black/40 after:to-black/0 after:to-50%':
                  post_type == 'person'
              }
            )}
          >
            <ImageParallax fill className="rounded-xs">
              {(ref) => (
                <Image
                  fill
                  ref={ref}
                  fit="cover"
                  size="large"
                  loading="lazy"
                  source={image}
                  alt={image?.alt}
                  className="rounded-xs"
                />
              )}
            </ImageParallax>
          </div>
        ) : null}

        <div className="flex items-end px-xs pb-sm pt-xs">
          <div className="grid gap-y-sm">
            {badge ? (
              <div className="flex">
                <span className="inline-flex rounded-full bg-white px-[1rem] pb-[.5rem] pt-[.7rem]">
                  <Typography variant="small" className="uppercase text-pink">
                    {badge}
                  </Typography>
                </span>
              </div>
            ) : null}

            {overline ? (
              <Typography variant="small" paragraph className="text-white">
                {overline}
              </Typography>
            ) : null}

            {title ? (
              <Typography
                variant="h6"
                component="h6"
                className={classNames({
                  'text-white': needsImage,
                  'text-blue': !needsImage
                })}
              >
                {title}
              </Typography>
            ) : null}

            {paragraph ? (
              <Typography
                variant="body"
                paragraph
                className={classNames({
                  'text-white': needsImage,
                  'text-neutral-20': !needsImage
                })}
              >
                {paragraph}
              </Typography>
            ) : null}

            {link ? (
              <div className="flex">
                <Link
                  href={link?.url || '/'}
                  target={link?.target}
                  className="text-pink"
                  textClassName={classNames({
                    'text-white': needsImage,
                    'text-pink': !needsImage
                  })}
                >
                  {link?.title}
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageCard
